<template>
  <div class="news_index">
    <page-header :title="$tc('news.name', 2)">
    </page-header>
    <page-content>
      <ntm-block :init-collapse="false">
        <template slot="title">
          {{ $t('news.aiNewsConfiguration') }}
          <premium-icon ref="premiumIcon" class="premium-icon ml-2" :style="{ position: 'static', height: '12px', color: isAiNewsLocked ? '#00B5AC' : 'currentColor' }" />
        </template>
        <feature-gate feature="ai-news">
          <form @submit.prevent="onSaveNewsSettings()">
            <formly-form :form="newsSettingsForm" :model="settingsModel" :fields="newsSettingsFields"></formly-form>
            <button class="btn btn-success push-15">{{ $t('save') }}</button>
          </form>
        </feature-gate>
      </ntm-block>
      <ntm-block :title="$tc('news.defaultCover')" :init-collapse="false">
        <template slot="options">
          <span>{{ $t('ratio') }}: <span class="font-weight-bold">16:10</span>, {{ $t('size') }}: <span class="font-weight-bold">800x500 - 1280x800</span></span>
        </template>
        <ntm-crop
          v-if="settingsModel.uuid"
          :entity-uuid="settingsModel.uuid"
          :aspect-ratio="16/10"
          entity-type="settings"
          conversion="lg"
          :max-width="1280"
          :max-height="800"
          :min-width="800"
          :min-height="500"
          collection="news">
        </ntm-crop>
      </ntm-block>
      <ntm-block :collapsable="false">
        <template slot="options">
          <div class="d-flex flex-wrap align-items-center mb-2">
            <button class="btn btn-sm mr-2" :class="{'btn-primary': isAiGenerated}" @click="toggleFilter('isAiGenerated')">
              {{ $t('news.aiGenerated') }}
            </button>
            <button class="btn btn-sm mr-2" :class="{'btn-primary': isHighlighted}" @click="toggleFilter('isHighlighted')">
              {{ $t('news.highlighted') }}
            </button>
            <button class="btn btn-sm mr-2" :class="{'btn-primary': isAnnounced}" @click="toggleFilter('isAnnounced')">
              {{ $t('news.announced') }}
            </button>
          </div>
          <input type="text" class="form-control" :placeholder="$t('search')"  v-model="search">
          <button @click="createEmptyNewsItemAndEdit()" type="button" class="btn btn-sm btn-success">{{$t('action.addNew')}}</button>
        </template>
        <div class="table-responsive">
          <ntm-table :show-columns="showColumns" :data="model" @pageChange="setPage" :loading="loading">
            <template #actions="{row}">
              <le-copy-to-clipboard class="mr-1" :text="getNewsItemWebsiteUrl(row)" :tooltip="$t('tooltip.copyWebsiteUrl')"></le-copy-to-clipboard>
              <router-link :to="{ name: 'news.edit', params: {uuid: row.uuid }}" class="btn btn-sm btn-primary" v-tooltip="$t('action.edit')">
                <i class="fa fa-pencil"></i>
              </router-link>
              <button type="button"
                    class="btn btn-sm btn-danger"
                    @click="deleteNews(row.uuid)"
                    v-tooltip="$t('action.delete')"><i class="fa fa-fw fa-trash"></i></button>
            </template>
          </ntm-table>
        </div>
      </ntm-block>
    </page-content>
  </div>
</template>
<script>
import basicIndexMixin from '@/mixins/basic.index.mixin'
import NewsService from '../../services/news.service'
import { useExternalLinks } from '@/use/useExternalLinks'
import { useCreateNewEntity } from '@/use/useCreateNewEntity'
import SettingsService from '../../services/settings.service'
import PremiumIcon from '@/components/svg/PremiumIcon.vue'
import FeatureGate from '@/components/shared/FeatureGate.vue'
import tippy from 'tippy.js'
import { useRouteQuery } from '@/use/useRouteQuery'

const baseNewsSettingsFields = [
  {
    key: 'hasAIGamesNews',
    type: 'boolean',
    templateOptions: {
      label: 'hasAIGamesNews'
    }
  },
  {
    key: 'aiGamesNewsHighlight',
    type: 'ntm-select',
    isString: true,
    options: [
      'ALL_GAMES',
      'CLOSE_AND_IMPORTANT_GAMES',
      'NONE_GAMES'
    ],
    templateOptions: {
      label: 'aiGamesNewsHighlight',
      translated: true
    }
  },
  {
    key: 'aiLeaguePreviousWeekSummaryNews',
    type: 'ntm-select',
    isString: true,
    options: ['OFF', 'EVERY_MONDAY', 'EVERY_TUESDAY', 'EVERY_WEDNESDAY', 'EVERY_THURSDAY', 'EVERY_FRIDAY', 'EVERY_SATURDAY', 'EVERY_SUNDAY'],
    templateOptions: {
      label: 'aiLeaguePreviousWeekSummaryNews',
      translated: true
    }
  }
]

export default {
  components: { PremiumIcon, FeatureGate },
  mixins: [basicIndexMixin],
  data () {
    return {
      settingsModel: {},
      newsSettingsForm: {},
      showColumns: [
        {
          name: 'title',
          prop: 'title'
        },
        {
          name: 'publishDate',
          prop: 'publishDate'
        },
        {
          name: 'isAiGenerated',
          prop: 'isAiGenerated',
          type: 'bool'
        },
        {
          name: 'isHighlighted',
          prop: 'isHighlighted',
          type: 'bool'
        },
        {
          name: 'isAnnounced',
          prop: 'isAnnounced',
          type: 'bool'
        }
      ]
    }
  },
  setup () {
    const { getNewsItemWebsiteUrl } = useExternalLinks()
    const { createEmptyNewsItemAndEdit } = useCreateNewEntity()
    const isAiGenerated = useRouteQuery('isAiGenerated')
    const isHighlighted = useRouteQuery('isHighlighted')
    const isAnnounced = useRouteQuery('isAnnounced')

    return {
      getNewsItemWebsiteUrl,
      createEmptyNewsItemAndEdit,
      isAiGenerated,
      isHighlighted,
      isAnnounced
    }
  },
  watch: {
    isAiNewsLocked (locked) {
      if (locked) {
        this.settingsModel.hasAIGamesNews = false
      }
      this.$nextTick(this.initPremiumIconTooltip)
    },
    isAiGenerated () { this.page = 1; this.fetchData() },
    isHighlighted () { this.page = 1; this.fetchData() },
    isAnnounced () { this.page = 1; this.fetchData() }
  },
  computed: {
    isAiNewsLocked () {
      return this.$store.getters.disabledFeatures.includes('ai-news')
    },
    newsSettingsFields () {
      return baseNewsSettingsFields
    }
  },
  beforeRouteEnter (to, from, next) {
    SettingsService.index().then((response) => {
      next(vm => vm.setModelData(response.data))
    })
  },
  mounted () {
    this.$nextTick(this.initPremiumIconTooltip)
    this.fetchData()
  },
  methods: {
    initPremiumIconTooltip () {
      const el = this.$refs.premiumIcon?.$el ?? this.$refs.premiumIcon
      if (!el) return
      if (el._tippy) el._tippy.destroy()
      if (this.isAiNewsLocked) {
        tippy(el, {
          content: this.$t('featureGate.not_available'),
          placement: 'top',
          theme: 'light',
          duration: [150, 100],
          arrow: true,
          zIndex: 99999999,
          onCreate (instance) {
            instance.popper.classList.add('feature-locked-tippy')
          }
        })
      } else {
        tippy(el, {
          content: this.$t('featureGate.available'),
          placement: 'top',
          theme: 'light',
          duration: [150, 100],
          arrow: true,
          zIndex: 99999999,
          onCreate (instance) {
            instance.popper.classList.add('feature-locked-tippy')
          }
        })
      }
    },
    fetchData () {
      if (this._fetchAbortController) {
        this._fetchAbortController.abort()
      }
      this._fetchAbortController = new AbortController()
      const signal = this._fetchAbortController.signal

      const filters = {}
      if (this.isAiGenerated) filters.isAiGenerated = true
      if (this.isHighlighted) filters.isHighlighted = true
      if (this.isAnnounced) filters.isAnnounced = true
      NewsService.index(this.search, this.page, filters).then((response) => {
        if (!signal.aborted) {
          this.setData(response.data)
        }
      })
    },
    toggleFilter (key) {
      this[key] = this[key] ? '' : true
    },
    setModelData (data) {
      this.settingsModel = {
        aiGamesNewsHighlight: 'ALL_GAMES',
        ...data
      }
    },
    onSaveNewsSettings () {
      SettingsService.storeNews({
        AINewsEnabled: true,
        hasAIGamesNews: this.settingsModel.hasAIGamesNews,
        aiGamesNewsHighlight: this.settingsModel.aiGamesNewsHighlight,
        aiLeaguePreviousWeekSummaryNews: this.settingsModel.aiLeaguePreviousWeekSummaryNews
      }).then(() => {
        this.newsSettingsForm.$errors = {}
        this.$store.commit('loaded')
      }).catch((err) => {
        this.newsSettingsForm.$errors = err.response.data.errors
      })
    },
    deleteNews (uuid) {
      NewsService.destroy(uuid).then((response) => {
        setTimeout(() => {
          this.fetchData()
        }, 300)
      }).finally(() => {
        this.$store.commit('loaded')
      })
    }
  }
}
</script>
